import { Link } from "react-router-dom";
import { Button, Card, Dropdown, Label } from "@heroui/react";
import { LogOut } from "lucide-react";
import { Container } from "@/shared/ui";
import { useUserStore, UserAvatar } from "@/entities/user";
import { useLogout } from "@/features/auth";

export interface HeaderProps {
  title?: string;
  subtitle?: string;
}

export const Header = ({
  title = "Kizi",
  subtitle = "Workspace",
}: HeaderProps) => {
  const { user, isAuthenticated } = useUserStore();
  const { logout, isLoggingOut } = useLogout();

  return (
    <header className="sticky top-0 z-40 w-full pt-4">
      <Container>
        <Card className="px-5 py-3 flex flex-row items-center justify-between backdrop-blur-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 select-none no-underline text-foreground">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-600 to-cyan-500 text-white shadow-md">
              <svg
                className="w-5 h-5"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
              </svg>
            </div>
            <div className="flex flex-col">
              <span className="text-base font-bold tracking-tight leading-tight">
                {title}
              </span>
              <span className="text-[11px] font-medium uppercase tracking-wider text-neutral-400">
                {subtitle}
              </span>
            </div>
          </Link>

          {/* Right User Avatar with Dropdown */}
          {isAuthenticated && user ? (
            <Dropdown>
              <Dropdown.Trigger className="rounded-full cursor-pointer focus:outline-none ring-2 ring-transparent hover:ring-indigo-500/30 transition-all">
                <UserAvatar user={user} size="sm" />
              </Dropdown.Trigger>
              <Dropdown.Popover>
                <div className="px-3 pt-3 pb-2">
                  <div className="flex items-center gap-2.5">
                    <UserAvatar user={user} size="sm" />
                    <div className="flex flex-col min-w-0 max-w-[180px]">
                      <p className="text-sm font-medium leading-tight truncate text-neutral-900">
                        {user.display_name || "Пользователь"}
                      </p>
                      <p className="text-xs text-neutral-400 leading-tight truncate mt-0.5" title={user.email}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                </div>
                <Dropdown.Menu
                  onAction={(key) => {
                    if (key === "logout") {
                      logout();
                    }
                  }}
                >
                  <Dropdown.Item
                    id="logout"
                    textValue="Выйти"
                    variant="danger"
                    isDisabled={isLoggingOut}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <Label>{isLoggingOut ? "Выход..." : "Выйти"}</Label>
                      <LogOut className="size-3.5 text-danger" />
                    </div>
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          ) : (
            <Link to="/login" className="no-underline">
              <Button size="sm" variant="outline" className="text-xs">
                Войти
              </Button>
            </Link>
          )}
        </Card>
      </Container>
    </header>
  );
};
